import type { GPHooks, GenerationInfo, StopReason } from "../types"

// Lightweight, zero-dep time/generation progress tracker built on hooks
export interface ProgressSnapshot {
  generation: number
  generationsObserved: number
  totalGenerations: number
  fractionByGenerations: number
  stopReason?: StopReason
  time: {
    elapsedMs: number
    initMs?: number
    lastGenMs?: number
    perGenMsAvg?: number
    estimatedTotalMs?: number
    remainingMs?: number
    fractionTime?: number
  }
}

export interface ProgressTrackerOptions<T> {
  totalGenerations: number
  onUpdate?: (p: ProgressSnapshot, info: GenerationInfo<T>) => void
}

export function createProgressTracker<T>(opts: ProgressTrackerOptions<T>) {
  const total = Math.max(1, Math.floor(opts.totalGenerations))
  let firstEndElapsed: number | undefined
  let prevEndElapsed: number | undefined
  let sumIntervals = 0
  let intervals = 0
  let lastInterval: number | undefined
  let latest: ProgressSnapshot = {
    generation: -1,
    generationsObserved: 0,
    totalGenerations: total,
    fractionByGenerations: 0,
    time: { elapsedMs: 0 },
  }

  function snapshotFrom(info: GenerationInfo<T>): ProgressSnapshot {
    if (firstEndElapsed === undefined) {
      firstEndElapsed = info.elapsedMs
      prevEndElapsed = info.elapsedMs
    } else if (prevEndElapsed !== undefined) {
      const dt = info.elapsedMs - prevEndElapsed
      if (dt >= 0) {
        sumIntervals += dt
        intervals += 1
        lastInterval = dt
      }
      prevEndElapsed = info.elapsedMs
    }

    const observed = info.generation + 1
    const fractionByGenerations = Math.max(0, Math.min(1, observed / total))
    const avg = intervals > 0 ? sumIntervals / intervals : undefined
    const initMs = firstEndElapsed
    const estimatedTotalMs =
      initMs !== undefined && avg !== undefined
        ? initMs + avg * (total - 1)
        : undefined
    const remainingMs =
      estimatedTotalMs !== undefined
        ? Math.max(0, estimatedTotalMs - info.elapsedMs)
        : undefined
    const fractionTime =
      estimatedTotalMs !== undefined
        ? Math.max(0, Math.min(1, info.elapsedMs / estimatedTotalMs))
        : undefined

    latest = {
      generation: info.generation,
      generationsObserved: observed,
      totalGenerations: total,
      fractionByGenerations,
      stopReason: info.stopReason,
      time: {
        elapsedMs: info.elapsedMs,
        initMs,
        lastGenMs: lastInterval,
        perGenMsAvg: avg,
        estimatedTotalMs,
        remainingMs,
        fractionTime,
      },
    }
    return latest
  }

  const hooks: GPHooks<T> = {
    onGenerationEnd(info) {
      const snap = snapshotFrom(info)
      opts.onUpdate?.(snap, info)
    },
  }

  return {
    hooks,
    get(): ProgressSnapshot {
      return latest
    },
    reset(): void {
      firstEndElapsed = undefined
      prevEndElapsed = undefined
      sumIntervals = 0
      intervals = 0
      lastInterval = undefined
      latest = {
        generation: -1,
        generationsObserved: 0,
        totalGenerations: total,
        fractionByGenerations: 0,
        time: { elapsedMs: 0 },
      }
    },
  }
}

export interface ProgressLoggerOptions {
  /** Log every N generations (default 1) */
  every?: number
  /** Print a one-time header before the first log (default true) */
  header?: boolean
  /** Custom sink for log lines (default console.log) */
  log?: (line: string) => void
}

/**
 * progressLogger: tiny, zero-dep progress logger for hooks or onGen.
 *
 * Usage:
 *  - As a hook: new GPLite(problem, { hooks: { onIteration: progressLogger() } })
 *  - As onGen: gp.run(progressLogger())
 */
export function progressLogger<T = unknown>(
  opts: ProgressLoggerOptions = {},
): (info: GenerationInfo<T>) => void {
  const every = Math.max(1, opts.every ?? 1)
  const log = opts.log ?? ((s: string) => console.log(s))
  const wantHeader = opts.header ?? true
  let headerPrinted = false

  const formatNum = (n: number) =>
    Number.isFinite(n) ? (n % 1 === 0 ? String(n) : n.toFixed(4)) : String(n)

  return function onGen(info: GenerationInfo<T>) {
    if (info.generation % every !== 0) return
    if (wantHeader && !headerPrinted) {
      log("generation\tbest\tavg\tpop\telapsedMs")
      headerPrinted = true
    }
    log(
      `${info.generation}\t${formatNum(info.bestFitness)}\t${formatNum(info.avgFitness)}\t${info.popSize}\t${info.elapsedMs}`,
    )
  }
}
